/**
 * The ABSOLUTE cycle span an exercise is actually prescribed in — and therefore the origin every
 * projection must be measured from.
 *
 * Ported from `move-with-me-frontend/.../plans/builder/model/cycleWindow.ts`, with `tempId`
 * lookups replaced by `slotToken` ones (the draft no longer carries a `tempId`; the slot token is
 * the exercise's identity here and on the wire alike).
 *
 * The backend materialises an exercise's set rows only for `[activationCycle .. end of its phase]`
 * and computes its progression baseline from the ACTIVATION cycle, not from the phase's first
 * cycle. An exercise present since the phase began has `activationCycle === phase.startCycle`, so
 * the two coincide for the common case; they diverge for an exercise added mid-plan. Reading the
 * origin off the phase for such an exercise shifts every projected cell by
 * `(activation − phaseStart)` cycles: the preview lies AND, once the trainer pins a manual
 * override, wrong values are persisted at wrong coordinates.
 *
 * Nothing in PR-A1's week view reads a cycle offset — it renders one phase's pattern, not the
 * per-cycle grid. This ships now because `responseToDraft` already depends on the same origin rule
 * to pick an exercise's authored base rows, and having the two derivations in one vocabulary is
 * what stops them drifting apart when PR-D builds the matrix on top.
 */

import type { PhaseDraft } from './planDraft.types';

/** An absolute cycle span: its first cycle (1-based) and how many cycles it runs. */
export interface CycleWindow {
  /** The window's FIRST absolute cycle — the authored base column of the matrix. */
  readonly startCycle: number;
  /** How many cycles the window spans. Always at least 1. */
  readonly cycleCount: number;
}

/**
 * The window an exercise occupies inside its phase: from its activation cycle — or the phase's
 * start when it has none, which is a brand-new exercise or a response predating the field — to the
 * phase's last cycle.
 *
 * The activation is CLAMPED into the phase span. The draft's phase boundaries are recomputed
 * locally whenever the trainer edits a cycle count or reorders phases, so a server-assigned
 * activation can fall outside the phase the exercise now sits in. Clamping keeps the window
 * non-empty and inside the coordinates the wire uses.
 */
export function exerciseCycleWindow(
  activationCycle: number | null,
  phaseStartCycle: number,
  phaseCycleCount: number,
): CycleWindow {
  const phaseStart = Math.max(1, phaseStartCycle);
  const phaseEnd = phaseStart + Math.max(1, phaseCycleCount) - 1;
  const startCycle =
    activationCycle == null ? phaseStart : Math.min(Math.max(activationCycle, phaseStart), phaseEnd);
  return { startCycle, cycleCount: phaseEnd - startCycle + 1 };
}

/**
 * Every phase's absolute FIRST cycle, index-aligned with `phases` — the walk each lookup below is
 * one early return out of, written once.
 *
 * **It recomputes rather than reading `PhaseDraft.startCycle`, and that is the whole reason it
 * exists.** The captured field is the SERVER's: it is correct on a freshly loaded tree and stale the
 * instant a trainer changes a cycle count, adds a phase or deletes one. The phase operations restamp
 * the field after every structural edit so the two agree again, and this walk is what they restamp
 * FROM — so a gate asking "is this phase inside the consumed prefix?" is right even on a tree that
 * has not been through those operations at all.
 *
 * A phase's cycle count is floored at 1 the same way every other reader here floors it: a zero would
 * put two phases on one start cycle and make the coordinate ambiguous.
 */
export function phaseStartCycles(phases: readonly PhaseDraft[]): number[] {
  const starts: number[] = [];
  let phaseStart = 1;
  for (const phase of phases) {
    starts.push(phaseStart);
    phaseStart += Math.max(1, phase.cycles);
  }
  return starts;
}

/**
 * The absolute span of the PHASE a day sits in — the frame every exercise of that day resolves its
 * own window inside. `null` when the token is not in the draft.
 *
 * A day has no activation of its own: every day of a phase exists in every cycle of it. So this is
 * the phase's span verbatim, and it exists as its own function because the alternative — a surface
 * walking `draft.phases` to add up cycle counts — is the coordinate rule living in a second place.
 */
export function findDayPhaseWindow(
  phases: readonly PhaseDraft[],
  daySlotToken: string,
): CycleWindow | null {
  const starts = phaseStartCycles(phases);
  for (const [index, phase] of phases.entries()) {
    if (phase.days.some((day) => day.slotToken === daySlotToken)) {
      return { startCycle: starts[index] ?? 1, cycleCount: Math.max(1, phase.cycles) };
    }
  }
  return null;
}

/**
 * Locate an exercise anywhere in the draft by its slot token and resolve its cycle window, walking
 * the phases to accumulate each one's absolute start cycle: phase 1 starts at cycle 1, and every
 * later phase starts after the previous one's cycles. `null` when the token is not in the draft.
 *
 * The walk recomputes each phase's start rather than reading `PhaseDraft.startCycle`, because the
 * captured value is the SERVER's and goes stale the moment the trainer changes a cycle count. The
 * two agree exactly on a freshly loaded tree.
 */
export function findExerciseCycleWindow(
  phases: readonly PhaseDraft[],
  exerciseSlotToken: string,
): CycleWindow | null {
  let phaseStart = 1;
  for (const phase of phases) {
    const exercise = phase.days
      .flatMap((day) => day.exercises)
      .find((candidate) => candidate.slotToken === exerciseSlotToken);
    if (exercise) return exerciseCycleWindow(exercise.activationCycle, phaseStart, phase.cycles);
    phaseStart += Math.max(1, phase.cycles);
  }
  return null;
}
